import React, { useState } from 'react'
import { Switch, CircularProgress, Button, Dialog } from '@material-ui/core'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import MuiDialogContent from '@material-ui/core/DialogContent'
import MuiDialogActions from '@material-ui/core/DialogActions'
import Dropdown from 'components/MVP/Dropdown/Dropdown'
import { useStyles } from './ManageReceivers.style'
import { NotificationSettingsData } from '../AlertType/AlertType'
import kebabCase from 'lodash/kebabCase'
import AppState from 'config/store/AppState'
import notificationSettingsStore from 'config/store/NotificationSettingsStore'
import ConfirmDialog from 'components/MVP/Dialog/ConfirmDialog'

interface ManageReceiversProps {
  alertType: string
  status: string
  isFetching: boolean
  isRefetchError: boolean
}

export const alertTypeDefaultFrequency: { [key: string]: string } = {
  'Performance Breakdown Alert': 'Weekly',
  'Missed Call Alert': 'Real-time',
  'En Route Alert': 'Weekly',
  'First Opportunity Alert': 'Real-time',
  'New Appointment Call Alert': 'Real-time',
  'New Opportunity Call Alert': 'Real-time',
}

export const alertTypeFrequencyOptions: { [key: string]: string[] } = {
  'Performance Breakdown Alert': ['Daily', 'Weekly'],
  'Missed Call Alert': ['Real-time', 'Daily', 'Weekly'],
  'En Route Alert': ['Weekly'],
  'First Opportunity Alert': ['Real-time'],
  'New Appointment Call Alert': ['Real-time', 'Daily', 'Weekly'],
  'New Opportunity Call Alert': ['Real-time', 'Daily', 'Weekly'],
}

const ManageReceivers: React.FC<ManageReceiversProps> = ({
  alertType,
  isFetching,
  isRefetchError,
  status,
}) => {
  const classes = useStyles()
  const [openDialog, setOpenDialog] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const getFilteredReceiversSettings = (
    _map: Map<string, NotificationSettingsData>,
    alertType: string
  ) => {
    return new Map(
      Array.from(_map).filter(([_key, value]) => {
        if (
          value.alertType === alertType &&
          value.contactId !== AppState.contactId
        ) {
          return true
        }

        return false
      })
    )
  }

  const alertTypeOptions = alertTypeFrequencyOptions[alertType]
  const receiversData = new Map(
    getFilteredReceiversSettings(notificationSettingsStore.settingsData, alertType)
  )

  const handleApply = () => {
    setOpenDialog(false)
  }

  const onReceiverFrequencyChange = (
    value: NotificationSettingsData,
    frequency: string
  ) => {
    const key = kebabCase(alertType + '-' + value.contactId)
    const _value = { ...value, frequency: frequency }
    notificationSettingsStore.setSettingsData(key, _value)
  }

  const onReceiverEmailChange = (
    value: NotificationSettingsData,
    checked: boolean
  ) => {
    const key = kebabCase(alertType + '-' + value.contactId)
    const _value = { ...value, email: checked }
    notificationSettingsStore.setSettingsData(key, _value)
  }

  const onReceiverSmsChange = (
    value: NotificationSettingsData,
    checked: boolean
  ) => {
    const key = kebabCase(alertType + '-' + value.contactId)
    const _value = { ...value, sms: checked }
    notificationSettingsStore.setSettingsData(key, _value)
  }

  const handleShowModal = (alertType: string) => {
    setOpenDialog(true)
  }

  const handleCloseModal = () => {
    setOpenDialog(false)
  }

  return (
    <>
      <Button
        onClick={() => handleShowModal(alertType)}
        variant="outlined"
        color="primary"
      >
        Manage Receivers
      </Button>
      <Dialog open={openDialog} fullWidth={true} maxWidth="md">
        <MuiDialogTitle>
          <h4 className={classes.modalTitle}>{`${alertType} Receivers`}</h4>
        </MuiDialogTitle>
        <MuiDialogContent>
          <Body
            status={status}
            isFetching={isFetching}
            isRefetchError={isRefetchError}
            contacts={receiversData}
            onReceiverFrequencyChange={onReceiverFrequencyChange}
            onReceiverEmailChange={onReceiverEmailChange}
            onReceiverSmsChange={onReceiverSmsChange}
            alertTypeOptions={alertTypeOptions}
            alertType={alertType}
          />
        </MuiDialogContent>
        <MuiDialogActions>
          <Footer
            handleApply={handleApply}
            setConfirmCancel={setConfirmCancel}
          />
        </MuiDialogActions>
      </Dialog>
      <ConfirmCancelDialog
        setConfirmCancel={setConfirmCancel}
        handleCloseModal={handleCloseModal}
        confirmCancel={confirmCancel}
      />
    </>
  )
}

type BodyProps = {
  contacts: Map<string, NotificationSettingsData>
  onReceiverFrequencyChange: (
    value: NotificationSettingsData,
    frequency: string
  ) => void
  onReceiverEmailChange: (
    value: NotificationSettingsData,
    checked: boolean
  ) => void
  onReceiverSmsChange: (
    value: NotificationSettingsData,
    checked: boolean
  ) => void
  status: string
  isFetching: boolean
  isRefetchError: boolean
  alertTypeOptions: string[]
  alertType: string
}
const Body = ({
  contacts,
  onReceiverFrequencyChange,
  onReceiverEmailChange,
  onReceiverSmsChange,
  status,
  isFetching,
  isRefetchError,
  alertTypeOptions,
  alertType,
}: BodyProps) => {
  const classes = useStyles()

  if (status === 'loading' || isFetching) {
    return (
      <div>
        <CircularProgress size={14} className={classes.spinner} />
      </div>
    )
  }

  if (status === 'error' || isRefetchError) {
    return <div>Error fetching data</div>
  }

  return (
    <div className={classes.tableWrapper}>
      <div className={classes.tableHeader}>
        <div>Staff</div>
        <div>Receive alert from</div>
        <div>Frequency</div>
      </div>
      {Array.from(contacts.values()).map((value: NotificationSettingsData) => {
        return (
          <>
            <div className={classes.tableRow}>
              <div className={classes.col}>
                <div>
                  <label>{value.name}</label>
                  <div
                    style={{ fontSize: '0.85em', color: '#888', marginTop: 4 }}
                  >
                    <span>{value.emailAddress}</span>
                    {value.emailAddress && value.phoneNumber ? ' | ' : ''}
                    <span>{value.phoneNumber}</span>
                  </div>
                </div>
              </div>
              <div className={classes.col}>
                <div className={classes.switchWrapper}>
                  <Switch
                    checked={value.email}
                    color="primary"
                    name="contact_email"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onReceiverEmailChange(value, e.target.checked)
                    }
                  />
                  <label>Email</label>
                </div>
                <div className={classes.switchWrapper}>
                  <Switch
                    checked={value.sms}
                    color="primary"
                    name="contact_sms"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onReceiverSmsChange(value, e.target.checked)
                    }
                  />
                  <label>SMS</label>
                </div>
              </div>
              <div className={classes.col}>
                <Dropdown
                  name="contact_frequency"
                  value={
                    value.frequency === ''
                      ? alertTypeDefaultFrequency[alertType]
                      : value.frequency
                  }
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    onReceiverFrequencyChange(value, e.target.value)
                  }
                  options={alertTypeOptions ?? ['Real-time', 'Daily', 'Weekly']}
                  placeholder="Choose Frequency"
                />
              </div>
            </div>
          </>
        )
      })}
    </div>
  )
}

type FooterProps = {
  handleApply: () => void
  setConfirmCancel: (isOpen: boolean) => void
}

const Footer = ({ handleApply, setConfirmCancel }: FooterProps) => {
  const classes = useStyles()
  return (
    <>
      <div className={classes.footerActions}>
        <Button
          onClick={() => setConfirmCancel(true)}
          variant="outlined"
          color="primary"
          style={{ marginRight: 8 }}
        >
          Cancel
        </Button>
        <Button onClick={handleApply} variant="contained" color="primary">
          Apply
        </Button>
      </div>
    </>
  )
}

interface ConfirmCancelDialogProps {
  setConfirmCancel: (isOpen: boolean) => void
  confirmCancel: boolean
  handleCloseModal: () => void
}

const ConfirmCancelDialog = ({
  setConfirmCancel,
  confirmCancel,
  handleCloseModal,
}: ConfirmCancelDialogProps) => {
  return (
    <ConfirmDialog
      cancelLabel="Keep editing"
      confirmLabel="Discard changes"
      onCancel={() => {
        setConfirmCancel(false)
      }}
      onConfirm={() => {
        setConfirmCancel(false)
        handleCloseModal()
      }}
      children={
        'You will loose all the changes made to Manage Receivers dialog.'
      }
      hideActions={false}
      open={confirmCancel}
      title={'Discard changes?'}
    />
  )
}

export default ManageReceivers
